interface AcceptableUsePolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function AcceptableUsePolicyModal({
  isOpen,
  onClose,
}: AcceptableUsePolicyModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full sm:max-w-[600px] max-h-[85vh] bg-bg-page-body rounded-t-[16px] sm:rounded-[16px] shadow-xl flex flex-col overflow-hidden mx-0 sm:mx-[16px]">
        <div className="flex items-center justify-between px-[16px] py-[14px] border-b border-border-subtle shrink-0">
          <h2 className="text-text-base text-[16px] font-bold">
            Acceptable Use Policy
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="material-symbols-outlined text-[22px] text-text-subtle"
          >
            close
          </button>
        </div>

        <div className="px-[16px] py-[16px] overflow-y-auto">
          <div className="text-text-base space-y-4">
            <div>
              <h3 className="text-[14px] font-bold mb-2">
                ACCEPTABLE USE POLICY (AUP)
              </h3>
              <p className="text-[11px] text-text-subtle mb-2">
                Effective Date: 1 July 2026
                <br />
                Last Updated: 30 September 2026
              </p>
            </div>

            <div className="space-y-4 text-[12px] leading-relaxed">
              <div>
                <h4 className="text-[13px] font-semibold mb-2">1. Purpose</h4>
                <p>
                  This Acceptable Use Policy (&ldquo;AUP&rdquo;) defines the
                  standards applicable to the use of products and services
                  offered by <strong>TYLT FINTECH INC.</strong> (&ldquo;Tylt&rdquo;,
                  &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;).
                </p>
                <p className="mt-2">
                  TYLT FINTECH INC. is a Canadian Money Services Business
                  registered with the Financial Transactions and Reports Analysis
                  Centre of Canada (&ldquo;FINTRAC&rdquo;) under MSB Registration
                  No. C10001670.
                </p>
                <p className="mt-2">
                  This AUP is designed to ensure that all users, businesses,
                  integrators, partners, affiliates, platforms, counterparties and
                  other persons accessing or using Tylt&rsquo;s payment, transfer,
                  ramp, virtual currency and crypto-asset infrastructure do so in
                  a lawful and compliant manner.
                </p>
                <p className="mt-2">
                  Users may access Tylt&rsquo;s services directly through
                  Tylt&rsquo;s website and exchange interface, including
                  exchange.tylt.money, or through approved integrators, partners,
                  affiliates, platforms, applications or other third-party
                  integrations.
                </p>
                <p className="mt-2">
                  By accessing or using Tylt&rsquo;s services, you agree to
                  comply with this AUP, Tylt&rsquo;s applicable Terms &amp;
                  Conditions, Risk Disclosure, Privacy Policy, product-specific
                  eligibility requirements, and all applicable laws and
                  regulations.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  2. Prohibited Activities
                </h4>
                <p>
                  Users must not use, attempt to use, or facilitate the use of
                  Tylt&rsquo;s services to engage in, promote, support, process
                  or facilitate any activity that is unlawful, fraudulent,
                  deceptive, abusive, prohibited or otherwise in violation of
                  this AUP.
                </p>
                <p className="mt-2">
                  Prohibited activities include, without limitation, the
                  following.
                </p>
                <p className="mt-3 font-semibold">
                  2.1 Unlawful or Prohibited Financial Activity
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    Money laundering, terrorist financing, proliferation
                    financing, sanctions evasion or proceeds-of-crime activity.
                  </li>
                  <li>
                    Fraudulent, deceptive or misleading schemes, scams,
                    impersonation, phishing, pyramid schemes or identity theft.
                  </li>
                  <li>
                    Use of stolen, compromised, unauthorised or fraudulently
                    obtained payment credentials, accounts, wallets, identities
                    or digital assets.
                  </li>
                  <li>
                    Structuring, layering, smurfing or other activity intended
                    to avoid reporting, verification or monitoring requirements.
                  </li>
                  <li>
                    Concealing, misrepresenting or obscuring the source,
                    ownership, beneficial ownership, purpose, nature, origin or
                    destination of funds, virtual currency, wallets,
                    counterparties or transactions.
                  </li>
                  <li>
                    Ransomware, darknet market activity, sanctioned wallet
                    activity or other transactions associated with illicit
                    finance.
                  </li>
                  <li>
                    Provision of unlicensed or unlawful financial, investment,
                    payment, remittance, lending, securities, derivatives,
                    brokerage, foreign exchange, virtual currency or crypto-asset
                    services.
                  </li>
                </ul>
                <p className="mt-3 font-semibold">
                  2.2 Illegal Goods, Services or Content
                </p>
                <p className="mt-2">
                  Users must not use Tylt&rsquo;s services in connection with
                  the sale, purchase, distribution, financing or facilitation of:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    Counterfeit, pirated, stolen or infringing goods, software,
                    services or content;
                  </li>
                  <li>Illegal narcotics or controlled substances;</li>
                  <li>Illegal weapons or ammunition;</li>
                  <li>Human trafficking;</li>
                  <li>Child sexual exploitation or abuse;</li>
                  <li>Illegal sexual services;</li>
                  <li>Illegal or unlicensed gambling; or</li>
                  <li>
                    Any other goods, services or activities prohibited by
                    applicable law.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  3. Restricted Business Categories
                </h4>
                <p>
                  The following categories are prohibited or restricted unless
                  expressly authorised in writing by Tylt&rsquo;s Compliance team
                  and all applicable legal, regulatory, licensing and partner
                  requirements have been satisfied:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    Adult content, escort services or sexually explicit services;
                  </li>
                  <li>
                    Gambling, betting, lotteries, casinos, gaming or wagering
                    operations;
                  </li>
                  <li>
                    Narcotics, controlled substances, drug paraphernalia or
                    related products, including CBD where restricted;
                  </li>
                  <li>
                    Weapons, ammunition, military goods, defence-related goods
                    or controlled dual-use goods;
                  </li>
                  <li>
                    High-risk investment products, binary options, unregulated
                    foreign exchange, contracts for difference, brokerage
                    platforms or speculative investment schemes;
                  </li>
                  <li>
                    Charitable, political, religious or crowdfunding activities
                    where appropriate authorisation or verification is
                    unavailable;
                  </li>
                  <li>
                    Shell companies, front companies, bearer-share entities,
                    opaque nominee arrangements or entities with unverifiable
                    beneficial ownership;
                  </li>
                  <li>
                    Privacy coins, mixers, tumblers or services primarily
                    designed to anonymise, disguise or obscure transactions;
                  </li>
                  <li>
                    NFT, token or digital-asset projects associated with fraud,
                    wash trading, sanctions evasion, market manipulation or money
                    laundering;
                  </li>
                  <li>
                    Unlicensed money services businesses, virtual asset service
                    providers, crypto exchanges, payment processors, remittance
                    businesses or other regulated financial intermediaries; and
                  </li>
                  <li>
                    Any other activity that Tylt determines presents unacceptable
                    legal, regulatory, sanctions, AML/ATF, fraud, financial,
                    reputational or operational risk.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  4. Gambling and Gaming
                </h4>
                <p>
                  Tylt does not permit its services to be used for illegal or
                  unlicensed gambling, betting, casino, lottery, gaming or
                  wagering activities.
                </p>
                <p className="mt-2">
                  Where Tylt supports a regulated gambling or gaming business,
                  that business must hold all licences, registrations and
                  approvals required in each jurisdiction in which it operates
                  or makes its services available.
                </p>
                <p className="mt-2">
                  Tylt may restrict or discontinue services where it determines
                  that a gambling or gaming business is operating outside the
                  scope of its applicable licences or regulatory permissions.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  5. Sanctions and Jurisdictional Restrictions
                </h4>
                <p>
                  Tylt does not provide services to or for the benefit of
                  individuals, entities, businesses, wallets, counterparties or
                  jurisdictions where doing so would violate applicable
                  sanctions, asset freezes, trade restrictions or other legal
                  prohibitions.
                </p>
                <p className="mt-2">
                  Tylt may screen users, counterparties, transactions and wallets
                  against applicable sanctions and restricted-party lists,
                  including Canadian, United Nations, OFAC, European Union and
                  United Kingdom sanctions where relevant.
                </p>
                <p className="mt-2">
                  Tylt may restrict, delay, reject, freeze, suspend or report
                  transactions involving:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Sanctioned individuals, entities, wallets or jurisdictions;</li>
                  <li>Persons owned or controlled by sanctioned persons;</li>
                  <li>Countries or regions subject to comprehensive sanctions;</li>
                  <li>High-risk or non-cooperative jurisdictions;</li>
                  <li>
                    Wallets exposed to sanctioned activity, terrorist financing,
                    ransomware, darknet markets, fraud, scams, mixers or
                    tumblers; or
                  </li>
                  <li>Other prohibited or high-risk activity.</li>
                </ul>
                <p className="mt-2">
                  Tylt may also apply enhanced controls to jurisdictions
                  identified by FATF or relevant regulators as high-risk,
                  non-cooperative or subject to increased monitoring.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  6. Geographic and Eligibility Restrictions
                </h4>
                <p>
                  The availability of Tylt&rsquo;s services may vary by
                  jurisdiction, product, payment method, payment rail, asset,
                  wallet type and transaction type.
                </p>
                <p className="mt-2">Users must not:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    Access or use a Service where it is not available or legally
                    permitted;
                  </li>
                  <li>
                    Misrepresent their country of residence, domicile, place of
                    establishment or location;
                  </li>
                  <li>Provide false or misleading geographic information;</li>
                  <li>
                    Use VPNs, proxies, location-spoofing tools or other methods
                    for the purpose of circumventing jurisdictional restrictions;
                    or
                  </li>
                  <li>
                    Enable another person to access a Service where that person
                    is not eligible to do so.
                  </li>
                </ul>
                <p className="mt-2">
                  Certain Tylt products, including particular Open Banking
                  services, may be subject to additional geographic eligibility
                  restrictions applicable to the European Union, European
                  Economic Area (&ldquo;EU/EEA&rdquo;) and United Kingdom
                  (&ldquo;UK&rdquo;).
                </p>
                <p className="mt-2">
                  Where an eligibility declaration is required, all information
                  provided must be accurate, complete and not misleading.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  7. Reporting Violations
                </h4>
                <p>
                  Suspected breaches of this AUP should be reported promptly to:
                </p>
                <p className="mt-2">
                  <a
                    href="mailto:compliance@tylt.money"
                    className="text-icon-blue-dark underline"
                  >
                    compliance@tylt.money
                  </a>
                </p>
                <p className="mt-2">
                  Tylt will review reports and take appropriate action consistent
                  with its legal, regulatory, compliance and operational
                  obligations.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">8. Amendments</h4>
                <p>
                  Tylt may amend this AUP from time to time to reflect changes in
                  laws, regulations, regulatory expectations, sanctions
                  requirements, payment partner requirements, product design or
                  internal risk policies.
                </p>
                <p className="mt-2">
                  Updated versions may be published on Tylt&rsquo;s website, made
                  available through the relevant platform or otherwise notified.
                </p>
                <p className="mt-2">
                  Continued use of Tylt&rsquo;s services after the applicable
                  effective date constitutes acceptance of the revised AUP to the
                  extent permitted by law.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">9. Contact</h4>
                <p>
                  <strong>TYLT FINTECH INC.</strong>
                </p>
                <p className="mt-2">Registered Address:</p>
                <p>2-1130 HACHEY AVE</p>
                <p>COQUITLAM, BC</p>
                <p>V3K 2H4</p>
                <p>CANADA</p>
                <p className="mt-2">Company Registration No.: BC1571749</p>
                <p>FINTRAC MSB Registration No.: C10001670</p>
                <p>
                  Email:{" "}
                  <a
                    href="mailto:compliance@tylt.money"
                    className="text-icon-blue-dark underline"
                  >
                    compliance@tylt.money
                  </a>
                </p>
                <p>
                  Website:{" "}
                  <a
                    href="https://www.tylt.money"
                    className="text-icon-blue-dark underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    www.tylt.money
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AcceptableUsePolicyModal;
